import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Shield, FileText, Building, Search, ShoppingCart, ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Button } from './ui/button';

const ServicesSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const services = [
    {
      icon: Shield,
      title: 'Trademark Registration',
      description: 'Secure your brand identity with comprehensive trademark registration services. Protect your business name, logo, and intellectual property.',
      link: '/services/trademark-registration',
      color: 'bg-[#C0392B]/10 text-[#C0392B]'
    },
    {
      icon: FileText,
      title: 'Copyright Registration',
      description: 'Protect your creative works and intellectual property with official copyright registration. Secure your rights today.',
      link: '/services/copyright-registration',
      color: 'bg-[#7F8C8D]/10 text-[#7F8C8D]'
    },
    {
      icon: Building,
      title: 'Company Formation',
      description: 'Start your business with expert company formation services. We handle all the paperwork so you can focus on growth.',
      link: '/services/company-formation',
      color: 'bg-[#C0392B]/10 text-[#C0392B]'
    },
    {
      icon: Search,
      title: 'Comprehensive Trademark Search',
      description: 'Ensure your trademark is available with our thorough search and analysis. Avoid costly conflicts before filing.',
      link: '/services/trademark-search',
      color: 'bg-[#7F8C8D]/10 text-[#7F8C8D]'
    },
    {
      icon: ShoppingCart,
      title: 'Amazon Brand Registry',
      description: 'Protect your brand on Amazon with our specialized brand registry assistance. Gain control over your product listings.',
      link: '/services/amazon-brand-registry',
      color: 'bg-[#C0392B]/10 text-[#C0392B]'
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + services.length) % services.length);
  };

  // Auto-slide effect
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        nextSlide();
      }, 4000); // Change slide every 4 seconds

      return () => clearInterval(interval);
    }
  }, [currentIndex, isPaused]);

  // Calculate which services to show (current, prev, next for desktop)
  const getVisibleServices = () => {
    const prev = (currentIndex - 1 + services.length) % services.length;
    const next = (currentIndex + 1) % services.length;
    return [prev, currentIndex, next];
  };

  const visibleIndices = getVisibleServices();

  return (
    <section className="py-20 bg-gradient-to-br from-white via-[#FDFEFE] to-[#FEF5F5] relative overflow-hidden">
      {/* Enhanced Decorative Elements */}
      <div className="absolute top-10 right-10 w-96 h-96 bg-[#C0392B]/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#7F8C8D]/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#C0392B]/3 to-[#7F8C8D]/3 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-6 max-w-[1320px] relative z-10">
        {/* Section Header with Badge */}
        <div className="text-center mb-16 space-y-6 animate-in fade-in slide-in-from-top duration-700">
          <div className="inline-block">
            <span className="bg-[#C0392B]/10 text-[#C0392B] px-4 py-2 rounded-full text-sm font-semibold">
              5 Essential Services
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
            Our Professional Services
          </h2>
          <p className="text-xl text-[#2C3E50] max-w-3xl mx-auto leading-relaxed">
            Comprehensive intellectual property and business services tailored to protect and grow your brand
          </p>
        </div>

        {/* Services Carousel */}
        <div 
          className="relative max-w-[1250px] mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Desktop View - 3 cards */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-6 mb-8">
            {visibleIndices.map((serviceIndex, idx) => {
              const service = services[serviceIndex];
              const Icon = service.icon;
              const isCenter = idx === 1;
              
              return (
                <Card 
                  key={serviceIndex}
                  className={`group hover:shadow-2xl transition-all duration-700 border-2 hover:border-[#C0392B] cursor-pointer overflow-hidden relative ${
                    isCenter 
                      ? 'scale-105 shadow-2xl border-[#C0392B]/30 bg-gradient-to-br from-white to-[#FDFEFE]' 
                      : 'border-[#7F8C8D]/20 opacity-95 hover:opacity-100'
                  }`}
                >
                  {/* Hover Effect Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C0392B]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardHeader className="relative z-10">
                    <div className={`w-20 h-20 rounded-2xl ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg icon-float`}>
                      <Icon className="w-10 h-10 group-hover:scale-125 transition-transform duration-500" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300 mb-3">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-[#2C3E50] transition-colors duration-300 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={service.link}>
                      <Button 
                        variant="ghost" 
                        className="text-[#C0392B] hover:text-[#A93226] hover:bg-[#C0392B]/5 p-0 h-auto font-semibold group/btn"
                      >
                        Learn More 
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-2 transition-all duration-300" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mobile/Tablet View - 1 card */}
          <div className="lg:hidden mb-8">
            <Card 
              className="group hover:shadow-2xl transition-all duration-500 border-[#7F8C8D]/20 hover:border-[#C0392B]/50 cursor-pointer overflow-hidden card-lift max-w-md mx-auto"
            >
              <CardHeader>
                <div className={`w-16 h-16 rounded-xl ${services[currentIndex].color} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                  {React.createElement(services[currentIndex].icon, {
                    className: "w-8 h-8 group-hover:scale-125 transition-transform duration-500"
                  })}
                </div>
                <CardTitle className="text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">
                  {services[currentIndex].title}
                </CardTitle>
                <CardDescription className="text-[#2C3E50] transition-colors duration-300">
                  {services[currentIndex].description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to={services[currentIndex].link}>
                  <Button 
                    variant="ghost" 
                    className="text-[#C0392B] hover:text-[#A93226] hover:bg-[#C0392B]/5 p-0 h-auto font-semibold group/btn"
                  >
                    Learn More 
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-2 transition-all duration-300" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Navigation Controls */}
          <div className="flex justify-center items-center space-x-6 mt-12">
            <Button
              onClick={prevSlide}
              variant="outline"
              size="icon"
              className="w-14 h-14 rounded-full border-2 border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B] hover:text-white transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>

            {/* Dots Indicator */}
            <div className="flex space-x-3 bg-white rounded-full px-6 py-3 shadow-lg border border-[#7F8C8D]/10">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-[#C0392B] w-10 h-3' 
                      : 'bg-[#7F8C8D]/30 hover:bg-[#7F8C8D]/60 w-3 h-3'
                  }`}
                  aria-label={`Go to service ${index + 1}`}
                />
              ))}
            </div>

            {/* Play/Pause Button */}
            <Button
              onClick={() => setIsPaused(!isPaused)}
              variant="outline"
              size="icon"
              className="w-14 h-14 rounded-full border-2 border-[#7F8C8D] text-[#7F8C8D] hover:bg-[#7F8C8D] hover:text-white transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl"
            >
              {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </Button>

            <Button
              onClick={nextSlide}
              variant="outline"
              size="icon"
              className="w-14 h-14 rounded-full border-2 border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B] hover:text-white transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;